import { RouteConstants } from '@/features/router/RouteConstants';
import { isWorkspaceAdmin } from '@/features/userInfo/hooks/useUserInfo';
import { useWorkspace } from '@/features/workspaces/hooks';
import { setCurrentWorkspace } from '@/features/workspaces/workspacesSlice';
import { EditableTitle } from '@/global/components';
import { Button, Input } from '@/lib';
import { Workspace } from '@/models/workspace';
import { useAppDispatch, RootState } from '@/reducers';
import { useGetWorkspaceQuery, useUpdateWorkspaceAiConsentMutation, useUpdateWorkspaceMutation } from '@/services/endpoints/workspaces';
import { FC } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LocalizationSelector } from './LocalizationSelector';
import { formatInTimeZone } from 'date-fns-tz';
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '@/features/TermsAndConditions/constants';
import { useGetTermsQuery } from '@/services/endpoints/support/endpoints';
import { Toggle } from '@/components/inputs';
import { Loadable } from '@/components/loaders';
import { ShieldCheckIcon } from '@heroicons/react/outline';

export const WorkspaceDetails: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const { termsAccepted } = useSelector((state: RootState) => state.userInfo);
  const isAdmin = isWorkspaceAdmin(workspace.id);
  const history = useHistory();
  const [updateWorkspace, { isLoading: isUpdating }] = useUpdateWorkspaceMutation();
  const [updateAiConsent, { isLoading: isAiDataAnalysisUpdating }] = useUpdateWorkspaceAiConsentMutation();
  const { data: workspaceDetails, isFetching: isWorkspaceDetailsFetching } = useGetWorkspaceQuery({
    workspaceId: workspace.id,
  });
  const isAiDataAnalysisEnabled = workspaceDetails?.aiUsageConsent ?? false;
  const { data: termsData } = useGetTermsQuery(undefined, { skip: !termsAccepted });
  const termsUrl = termsData?.documents.terms_and_conditions.url || TERMS_AND_CONDITIONS_URL;
  const privacyUrl = termsData?.documents.privacy_policy.url || PRIVACY_POLICY_URL;

  const handleUpdate = async (item: Workspace, newTitle: string) => {
    const updatedWorkspace = await updateWorkspace({
      workspaceId: item.id,
      workspace: {
        name: newTitle,
        slug: item.slug,
      },
    }).unwrap();
    dispatch(setCurrentWorkspace(updatedWorkspace));
  };

  const handleAiDataAnalysisToggle = async (enabled: boolean) => {
    try {
      await updateAiConsent({ workspaceId: workspace.id, granted: enabled }).unwrap();
    } catch {
      // query data remains unchanged until refetch
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-end justify-between gap-2'>
        <div className='flex flex-col gap-2'>
          <p className='mb-2 text-sm font-semibold text-secondary-700'>{t('settings:caption_workspace_name')}</p>
          <EditableTitle
            item={workspace}
            title={workspace?.name || ''}
            onUpdate={handleUpdate}
            isLoading={isUpdating}
            isEditable={isAdmin}
            successMessage={{
              title: t('workspaceUpdated'),
              message: t('workspaceUpdatedSuccessfully', { name: workspace?.name }),
            }}
            errorMessage={{
              title: t('workspaceUpdateError'),
              message: t('workspaceUpdateError'),
            }}
          />
        </div>
        <div className='flex flex-row items-end gap-2'>
          <span className='mb-2 text-sm font-normal text-secondary-700'>{t('workspaces:edit.slug')}</span>
          <Input disabled value={workspace?.slug} className='w-full' />
        </div>
      </div>

      {workspace?.companyName && (
        <>
          <p className='mt-16 text-sm font-normal text-secondary-700'>{t('settings:company_caption')}</p>
          <div className='flex flex-row items-end gap-4'>
            <span className=' text-sm font-semibold text-secondary-700'>{workspace.companyName}</span>
            <Button
              intent='link-color'
              text={t('settings:company_view_details')}
              className='p-0'
              onClick={() => history.push(`${RouteConstants.settings}/company`)}
            />
          </div>
        </>
      )}
      <div className='mt-4 flex flex-row items-end gap-4'>
        <LocalizationSelector />
      </div>
      <div className='mt-4 flex flex-col gap-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{t('settings:privacy_ai_section')}</p>
        <div className='flex items-center justify-between gap-4 rounded-xl border border-quaternary-200 bg-white-100 p-4 shadow-sm'>
          <div className='flex items-center gap-4'>
            <div className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600'>
              <ShieldCheckIcon className='size-5' aria-hidden='true' />
            </div>
            <div>
              <p className='text-sm font-semibold text-gray-900'>{t('settings:ai_data_analysis.title')}</p>
              <p className='text-sm font-normal text-gray-500'>{t('settings:ai_data_analysis.description')}</p>
            </div>
          </div>
          <Loadable className='h-5 shrink-0' isLoading={isAiDataAnalysisUpdating} spinnerClassName='size-5'>
            <Toggle
              aria-label={t('settings:ai_data_analysis.title')}
              checked={isAiDataAnalysisEnabled}
              disabled={!isAdmin || isAiDataAnalysisUpdating || isWorkspaceDetailsFetching}
              onChange={(e) => handleAiDataAnalysisToggle(e.target.checked)}
            />
          </Loadable>
        </div>
      </div>
      {termsAccepted && (
        <div className='mt-4 text-sm text-secondary-700'>
          <Trans
            i18nKey='settings:accepted_on'
            values={{ date: formatInTimeZone(new Date(termsAccepted), 'UTC', 'dd/MM/yyyy') }}
            components={{
              tc: <a href={termsUrl} target='_blank' rel='noopener noreferrer' className='text-blue-600 underline hover:text-blue-800' />,
              privacy: <a href={privacyUrl} target='_blank' rel='noopener noreferrer' className='text-blue-600 underline hover:text-blue-800' />,
            }}
          />
        </div>
      )}
    </div>
  );
};
